import { useCallback, useMemo } from "react";

import { useSearchParams } from "react-router-dom";

import { Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { WarehouseStockByProductVariants } from "@/features/warehouse-stocks/by-product-variants";
import { WarehouseStockByWarehouses } from "@/features/warehouse-stocks/by-warehouses";

const STOCK_VIEWS = {
  BY_PRODUCT_VARIANT: "by-product-variant",
  BY_WAREHOUSE: "by-warehouse",
};

// The page is URL-driven so stock can be deep-linked from anywhere that knows a
// SKU or a warehouse (e.g. transaction detail grids):
//   ?sku=PB&productVariantName=Paper%20Bag  -> product-variant view, preloaded
//   ?warehouseId=<id>&warehouseName=Store%20Halat -> warehouse view, preloaded
// The optional name params are display-only; the id/sku alone are enough to load.
export const WarehouseStocks = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const sku = searchParams.get("sku");
  const productVariantName = searchParams.get("productVariantName");
  const warehouseId = searchParams.get("warehouseId");
  const warehouseName = searchParams.get("warehouseName");
  const viewParam = searchParams.get("view");

  // An explicit ?view wins; otherwise infer the tab from whichever preselection
  // param is present, defaulting to the product-variant view.
  const stockView = useMemo(() => {
    if (
      viewParam === STOCK_VIEWS.BY_WAREHOUSE ||
      viewParam === STOCK_VIEWS.BY_PRODUCT_VARIANT
    ) {
      return viewParam;
    }
    if (sku) return STOCK_VIEWS.BY_PRODUCT_VARIANT;
    if (warehouseId) return STOCK_VIEWS.BY_WAREHOUSE;
    return STOCK_VIEWS.BY_PRODUCT_VARIANT;
  }, [viewParam, sku, warehouseId]);

  const handleStockViewChange = (_, newView) => {
    if (newView !== null) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", newView);
          return next;
        },
        { replace: true }
      );
    }
  };

  // Two-way sync: reflect a manual selection back into the URL so the current
  // view is shareable/bookmarkable. The name params are display-only. Selections
  // also pin `view` so the shared warehouseId param (a filter in the
  // product-variant view, the selection in the warehouse view) never flips the
  // active tab out from under the user.
  const handleProductVariantChange = useCallback(
    (productVariant) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", STOCK_VIEWS.BY_PRODUCT_VARIANT);
          if (productVariant?.sku) {
            next.set("sku", productVariant.sku);
            if (productVariant.name) {
              next.set("productVariantName", productVariant.name);
            } else {
              next.delete("productVariantName");
            }
          } else {
            next.delete("sku");
            next.delete("productVariantName");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setWarehouseInUrl = useCallback(
    (warehouse, view) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("view", view);
          if (warehouse?.id) {
            next.set("warehouseId", warehouse.id);
            if (warehouse.name) {
              next.set("warehouseName", warehouse.name);
            } else {
              next.delete("warehouseName");
            }
          } else {
            next.delete("warehouseId");
            next.delete("warehouseName");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleProductVariantWarehouseChange = useCallback(
    (warehouse) =>
      setWarehouseInUrl(warehouse, STOCK_VIEWS.BY_PRODUCT_VARIANT),
    [setWarehouseInUrl]
  );

  const handleWarehouseChange = useCallback(
    (warehouse) => setWarehouseInUrl(warehouse, STOCK_VIEWS.BY_WAREHOUSE),
    [setWarehouseInUrl]
  );

  const toolbarContent = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={stockView}
      onChange={handleStockViewChange}
    >
      <ToggleButton value={STOCK_VIEWS.BY_PRODUCT_VARIANT}>
        Produk Varian
      </ToggleButton>
      <ToggleButton value={STOCK_VIEWS.BY_WAREHOUSE}>Gudang</ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <PageContainer
      maxWidth="xl"
      breadcrumbs={[]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={3}>
        {stockView === STOCK_VIEWS.BY_PRODUCT_VARIANT ? (
          <WarehouseStockByProductVariants
            initialSku={sku}
            initialProductVariantName={productVariantName}
            initialWarehouseId={warehouseId}
            initialWarehouseName={warehouseName}
            onProductVariantChange={handleProductVariantChange}
            onWarehouseChange={handleProductVariantWarehouseChange}
          />
        ) : (
          <WarehouseStockByWarehouses
            initialWarehouseId={warehouseId}
            initialWarehouseName={warehouseName}
            onWarehouseChange={handleWarehouseChange}
          />
        )}
      </Stack>
    </PageContainer>
  );
};
